import React, { useState, useEffect } from 'react';
import {
  Target,
  ChevronDown,
  ChevronRight,
  CheckCircle2,
  Circle,
  Clock,
  Loader2,
  RefreshCw,
  Calendar,
  User,
  Tag,
  AlertTriangle,
  Pencil,
  X,
} from 'lucide-react';
import { apiService } from '../services/api';
import toast from 'react-hot-toast';

interface ActionItem {
  id: number;
  planId: number;
  actionItem: string;
  category: string;
  dueDate: string;
  assignee: string;
  status: 'pending' | 'in_progress' | 'completed';
  priority: 'high' | 'medium' | 'low';
  notes: string | null;
}

interface Plan {
  id: number;
  planName: string;
  description: string | null;
  month: string;
  status: 'active' | 'completed' | 'archived';
  createdAt: string;
  actionItems: ActionItem[];
}

const CATEGORY_COLORS: Record<string, string> = {
  Marketing:   'bg-purple-100 text-purple-700',
  Inventory:   'bg-blue-100 text-blue-700',
  Operations:  'bg-orange-100 text-orange-700',
  Sales:       'bg-green-100 text-green-700',
  Technology:  'bg-cyan-100 text-cyan-700',
  SEO:         'bg-pink-100 text-pink-700',
  Finance:     'bg-yellow-100 text-yellow-700',
};

const PRIORITY_COLORS: Record<string, string> = {
  high:   'text-red-600',
  medium: 'text-yellow-600',
  low:    'text-gray-400',
};

const STATUS_ICON: Record<string, React.ReactNode> = {
  completed:  <CheckCircle2 className="h-4 w-4 text-green-500" />,
  in_progress: <Clock className="h-4 w-4 text-blue-500" />,
  pending:    <Circle className="h-4 w-4 text-gray-400" />,
};

interface EditForm {
  actionItem: string;
  category: string;
  dueDate: string;
  assignee: string;
  priority: string;
  status: string;
  notes: string;
}

const PlansPage: React.FC = () => {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [updatingItem, setUpdatingItem] = useState<number | null>(null);
  const [editingItem, setEditingItem] = useState<ActionItem | null>(null);
  const [editForm, setEditForm] = useState<EditForm | null>(null);
  const [saving, setSaving] = useState(false);

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const data = await apiService.getPlans();
      setPlans(data);
      // Auto-expand the first active plan
      const active = data.find((p: Plan) => p.status === 'active');
      if (active) setExpanded(new Set([active.id]));
    } catch {
      toast.error('Failed to load plans');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPlans(); }, []);

  const toggleExpand = (id: number) => {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const cycleStatus = async (item: ActionItem) => {
    const next: Record<string, ActionItem['status']> = {
      pending: 'in_progress',
      in_progress: 'completed',
      completed: 'pending',
    };
    const newStatus = next[item.status];
    setUpdatingItem(item.id);
    try {
      await apiService.updateActionItemStatus(item.id, newStatus);
      setPlans(prev =>
        prev.map(p => ({
          ...p,
          actionItems: p.actionItems.map(a =>
            a.id === item.id ? { ...a, status: newStatus } : a
          ),
        }))
      );
    } catch {
      toast.error('Failed to update status');
    } finally {
      setUpdatingItem(null);
    }
  };

  const openEdit = (item: ActionItem) => {
    setEditingItem(item);
    setEditForm({
      actionItem: item.actionItem,
      category:   item.category ?? '',
      dueDate:    item.dueDate ? item.dueDate.slice(0, 10) : '',
      assignee:   item.assignee ?? '',
      priority:   item.priority,
      status:     item.status,
      notes:      item.notes ?? '',
    });
  };

  const saveEdit = async () => {
    if (!editingItem || !editForm) return;
    setSaving(true);
    try {
      await apiService.updateActionItem(editingItem.id, {
        actionItem: editForm.actionItem,
        category:   editForm.category   || undefined,
        dueDate:    editForm.dueDate    || undefined,
        assignee:   editForm.assignee   || undefined,
        priority:   editForm.priority,
        status:     editForm.status,
        notes:      editForm.notes      || undefined,
      });
      setPlans(prev => prev.map(p => ({
        ...p,
        actionItems: p.actionItems.map(a =>
          a.id === editingItem.id
            ? { ...a, ...editForm, dueDate: editForm.dueDate || a.dueDate } as ActionItem
            : a
        ),
      })));
      setEditingItem(null);
      setEditForm(null);
      toast.success('Task updated');
    } catch {
      toast.error('Failed to update task');
    } finally {
      setSaving(false);
    }
  };

  const stats = (items: ActionItem[]) => ({
    total: items.length,
    done: items.filter(i => i.status === 'completed').length,
    high: items.filter(i => i.priority === 'high' && i.status !== 'completed').length,
    overdue: items.filter(i =>
      i.status !== 'completed' && new Date(i.dueDate) < new Date()
    ).length,
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Target className="h-6 w-6 text-primary-500" />
            Monthly Plans
          </h1>
          <p className="text-sm text-gray-500 mt-1">Track goals and action items for each month</p>
        </div>
        <button
          onClick={fetchPlans}
          className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh
        </button>
      </div>

      {plans.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-xl border border-gray-200">
          <Target className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">No plans yet</p>
          <p className="text-sm text-gray-400 mt-1">
            Use <code className="bg-gray-100 px-1 rounded">/minto-plan</code> in Claude Code to create your first monthly plan
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {plans.map(plan => {
            const s = stats(plan.actionItems ?? []);
            const pct = s.total > 0 ? Math.round((s.done / s.total) * 100) : 0;
            const isOpen = expanded.has(plan.id);

            return (
              <div key={plan.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                {/* Plan Header */}
                <button
                  onClick={() => toggleExpand(plan.id)}
                  className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    {isOpen
                      ? <ChevronDown className="h-4 w-4 text-gray-500 shrink-0" />
                      : <ChevronRight className="h-4 w-4 text-gray-500 shrink-0" />
                    }
                    <div className="text-left">
                      <div className="flex items-center gap-2">
                        <h2 className="font-semibold text-gray-900">{plan.planName}</h2>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          plan.status === 'active'
                            ? 'bg-green-100 text-green-700'
                            : plan.status === 'completed'
                            ? 'bg-gray-100 text-gray-600'
                            : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {plan.status}
                        </span>
                      </div>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {plan.month} · {s.done}/{s.total} completed
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 shrink-0">
                    {s.overdue > 0 && (
                      <span className="flex items-center gap-1 text-xs text-red-600 font-medium">
                        <AlertTriangle className="h-3.5 w-3.5" />
                        {s.overdue} overdue
                      </span>
                    )}
                    {s.high > 0 && (
                      <span className="text-xs text-red-500 font-medium">{s.high} high priority</span>
                    )}
                    {/* Progress bar */}
                    <div className="flex items-center gap-2">
                      <div className="w-24 h-1.5 bg-gray-200 rounded-full">
                        <div
                          className="h-1.5 bg-primary-500 rounded-full transition-all"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <span className="text-xs text-gray-500 w-8">{pct}%</span>
                    </div>
                  </div>
                </button>

                {/* Action Items */}
                {isOpen && (
                  <div className="border-t border-gray-100">
                    {(plan.actionItems ?? []).length === 0 ? (
                      <p className="text-sm text-gray-400 px-5 py-6 text-center">No action items</p>
                    ) : (
                      <div className="divide-y divide-gray-50">
                        {['high', 'medium', 'low'].map(priority => {
                          const items = (plan.actionItems ?? []).filter(i => i.priority === priority);
                          if (items.length === 0) return null;
                          return (
                            <div key={priority}>
                              <div className="px-5 py-2 bg-gray-50">
                                <span className={`text-xs font-semibold uppercase tracking-wide ${PRIORITY_COLORS[priority]}`}>
                                  {priority} priority
                                </span>
                              </div>
                              {items.map(item => {
                                const isOverdue =
                                  item.status !== 'completed' &&
                                  new Date(item.dueDate) < new Date();

                                return (
                                  <div
                                    key={item.id}
                                    className={`flex items-start gap-3 px-5 py-3 hover:bg-gray-50 transition-colors ${
                                      item.status === 'completed' ? 'opacity-60' : ''
                                    }`}
                                  >
                                    {/* Status toggle */}
                                    <button
                                      onClick={() => cycleStatus(item)}
                                      disabled={updatingItem === item.id}
                                      className="mt-0.5 shrink-0"
                                      title="Click to cycle: pending → in progress → done"
                                    >
                                      {updatingItem === item.id
                                        ? <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                                        : STATUS_ICON[item.status]
                                      }
                                    </button>

                                    {/* Content */}
                                    <div className="flex-1 min-w-0">
                                      <p className={`text-sm text-gray-800 ${item.status === 'completed' ? 'line-through text-gray-400' : ''}`}>
                                        {item.actionItem}
                                      </p>
                                      <div className="flex items-center gap-3 mt-1 flex-wrap">
                                        {item.category && (
                                          <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${CATEGORY_COLORS[item.category] ?? 'bg-gray-100 text-gray-600'}`}>
                                            <Tag className="h-2.5 w-2.5 inline mr-0.5" />
                                            {item.category}
                                          </span>
                                        )}
                                        {item.assignee && (
                                          <span className="flex items-center gap-1 text-xs text-gray-500">
                                            <User className="h-3 w-3" />
                                            {item.assignee}
                                          </span>
                                        )}
                                        {item.dueDate && (
                                          <span className={`flex items-center gap-1 text-xs ${isOverdue ? 'text-red-500 font-medium' : 'text-gray-400'}`}>
                                            <Calendar className="h-3 w-3" />
                                            {isOverdue && 'Overdue · '}
                                            {new Date(item.dueDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                                          </span>
                                        )}
                                      </div>
                                    </div>

                                    {/* Edit button */}
                                    <button
                                      onClick={() => openEdit(item)}
                                      className="shrink-0 p-1 text-gray-300 hover:text-gray-500 transition-colors"
                                      title="Edit task"
                                    >
                                      <Pencil className="h-3.5 w-3.5" />
                                    </button>
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <p className="text-xs text-gray-400 text-center mt-6">
        Create plans using <code className="bg-gray-100 px-1 rounded">/minto-plan</code> in Claude Code
      </p>

      {/* ── Edit Modal ── */}
      {editingItem && editForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
            {/* Modal header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900">Edit Task</h3>
              <button onClick={() => { setEditingItem(null); setEditForm(null); }} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Form */}
            <div className="px-5 py-4 space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Task</label>
                <textarea
                  rows={2}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 resize-none"
                  value={editForm.actionItem}
                  onChange={e => setEditForm({ ...editForm, actionItem: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
                  <select
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
                    value={editForm.category}
                    onChange={e => setEditForm({ ...editForm, category: e.target.value })}
                  >
                    <option value="">— None —</option>
                    {Object.keys(CATEGORY_COLORS).map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Priority</label>
                  <select
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
                    value={editForm.priority}
                    onChange={e => setEditForm({ ...editForm, priority: e.target.value })}
                  >
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
                  <select
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
                    value={editForm.status}
                    onChange={e => setEditForm({ ...editForm, status: e.target.value })}
                  >
                    <option value="pending">Pending</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Due Date</label>
                  <input
                    type="date"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
                    value={editForm.dueDate}
                    onChange={e => setEditForm({ ...editForm, dueDate: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Assignee</label>
                <input
                  type="text"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
                  value={editForm.assignee}
                  onChange={e => setEditForm({ ...editForm, assignee: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
                <textarea
                  rows={2}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 resize-none"
                  value={editForm.notes}
                  onChange={e => setEditForm({ ...editForm, notes: e.target.value })}
                />
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-2 px-5 py-3 border-t border-gray-200">
              <button
                onClick={() => { setEditingItem(null); setEditForm(null); }}
                className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={saveEdit}
                disabled={saving || !editForm.actionItem.trim()}
                className="px-4 py-2 text-sm text-white bg-primary-500 rounded-lg hover:bg-primary-600 disabled:opacity-50 flex items-center gap-2"
              >
                {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlansPage;
